import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { i18n } from '@fs/zion-locale'
import { usePreference } from '@fs/zion-preferences'
import { useCanAddPersonalData, usePermission } from '@fs/zion-permissions'
import { useUser } from '@fs/zion-user'
import langUrlMap from './familyTreeLangUrlMap.json'

export default function useFamilyTreeLinks({ canSeeTemple } = {}) {
  const { t } = useTranslation()
  const treeCETTestPrefIsOn = usePreference('tree.cetTest')
  const { isOn: showChineseFamilyTree } = useFeatureFlag('home_header_zhFamilyTree')
  const { isOn: home_updateHeaderTreeNav } = useFeatureFlag('home_updateHeaderTreeNav')
  const { isOn: treeWeb_researcherTrees } = useFeatureFlag('treeWeb_researcherTrees', { pref: !!treeCETTestPrefIsOn })
  const { isOn: useFamilyTreeUrl } = useFeatureFlag('tree-overview_useFamilyTreeUrl')
  const { isOn: showCETs } = useFeatureFlag('home_header_cetsLink')
  const showManageTreesLabel = useFeatureFlag('treeWeb_manageTrees').isOn
  const { signedIn } = useUser()
  const [isAuthorized] = useCanAddPersonalData()
  const [canAccessFGT] = usePermission('CanAccessFamilyGroupTreesPermission', 'FamilyTree')

  if (home_updateHeaderTreeNav) {
    const treeLinks = {
      id: 'tree',
      text: t('nav.family-tree', 'Family Tree'),
      sub: [
        {
          id: 'overview',
          text: t('nav.overview', 'Overview'),
          url: useFamilyTreeUrl ? `/${i18n.language}${langUrlMap[i18n.language] || '/family-tree/'}` : '/tree/overview',
        },
        {
          id: 'overview-chinese',
          text: t('nav.overview', 'Overview'),
          url: '/zh/chinese/surnames/family-tree',
        },
        {
          id: 'pedigree',
          text: t('nav.family-tree.tree', 'Tree'),
          url: '/tree/pedigree',
        },
        { id: 'recents', text: t('nav.family-tree.recents', 'Recents'), url: '/tree/recents' },
        { id: 'find_in_tree', text: t('nav.family-tree.find', 'Find'), url: '/search/tree?from=tree' },
        { id: 'following', text: t('nav.family-tree.following', 'Following'), url: '/tree/following/' },
        {
          id: 'contributions',
          text: t('nav.family-tree.contributions', 'My Contributions'),
          url: '/tree/contributions',
        },
        {
          id: 'person-list',
          text: t('nav.family-tree.private-people', 'Person List'),
          url: '/tree/private-people',
        },
        {
          id: 'family-groups',
          text: t('nav.family-groups', 'Family Groups'),
          url: '/groups/family',
        },
        {
          id: 'research-trees',
          text: showCETs ? t('nav.CETs', 'CETs') : t('nav.research-trees', 'Research Trees'),
          url: '/groups/researcher',
        },
        {
          id: 'manage-trees',
          text: t('nav.manage-trees', 'Manage Trees'),
          url: '/groups/trees',
        },
      ],
    }

    const showChineseOverview = i18n.language === 'zh' && showChineseFamilyTree

    treeLinks.sub = treeLinks.sub.filter((link) => {
      let keep = true
      if (link.id === 'recents' && !signedIn) {
        keep = false
      }
      // show a different overview page if in chinese
      if (link.id === 'overview' && showChineseOverview) {
        keep = false
      }
      if (link.id === 'overview-chinese' && !showChineseOverview) {
        keep = false
      }
      if (
        link.id === 'family-groups' &&
        (!(isAuthorized && (canAccessFGT || canSeeTemple) && signedIn) || showManageTreesLabel)
      ) {
        keep = false
      }
      // show Research Trees in only en,es,pt when flag is turned on
      if (
        link.id === 'research-trees' &&
        (showManageTreesLabel ||
          !(isAuthorized && treeWeb_researcherTrees && signedIn && ['en', 'es', 'pt'].includes(i18n.language)))
      ) {
        keep = false
      }
      if (link.id === 'manage-trees' && (!(isAuthorized && signedIn) || !showManageTreesLabel)) {
        keep = false
      }

      return keep
    })

    return treeLinks
  }
  const treeLinks = {
    id: 'tree',
    text: t('nav.family-tree', 'Family Tree'),
    sub: [
      {
        id: 'overview',
        text: t('nav.overview', 'Overview'),
        url: useFamilyTreeUrl ? `/${i18n.language}/family-tree/` : '/tree/overview',
      },
      {
        id: 'overview-chinese',
        text: t('nav.overview', 'Overview'),
        url: '/zh/chinese/surnames/family-tree',
      },
      {
        id: 'pedigree',
        text: t('nav.family-tree.tree', 'Tree'),
        url: '/tree/pedigree',
      },
      { id: 'person', text: t('nav.family-tree.person', 'Person'), url: '/tree/person' },
      { id: 'recents', text: t('nav.family-tree.recents', 'Recents'), url: '/tree/recents' },
      { id: 'find_in_tree', text: t('nav.family-tree.find', 'Find'), url: '/search/tree?from=tree' },
      { id: 'following', text: t('nav.family-tree.following', 'Following'), url: '/tree/following/' },
      {
        id: 'contributions',
        text: t('nav.family-tree.contributions', 'My Contributions'),
        url: '/tree/contributions',
      },
      {
        id: 'family_groups_trees',
        text: t('nav.family-tree.family-groups-trees', 'Family Groups and Trees'),
        url: '/groups/family/',
      },
    ],
  }

  const showChineseOverview = i18n.language === 'zh' && showChineseFamilyTree

  treeLinks.sub = treeLinks.sub.filter((link) => {
    let keep = true
    if (link.id === 'recents' && !signedIn) {
      keep = false
    }
    // show a different overview page if in chinese
    if (link.id === 'overview' && showChineseOverview) {
      keep = false
    }
    if (link.id === 'overview-chinese' && !showChineseOverview) {
      keep = false
    }
    if (link.id === 'family_groups_trees' && !signedIn) {
      keep = false
    }

    return keep
  })

  return treeLinks
}
